import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import OrderForm from './OrderForm';
import ConfirmDialog from './ConfirmDialog';
import { UNSAVED_PROMPT_BUTTONS, UNSAVED_ORDER_PROMPT_MESSAGE } from '../constants/unsavedChangesPrompt';
import PostApprovalEditDialog from './PostApprovalEditDialog';
import { isPostApprovalStatus, buildEditReason } from '../constants/postApprovalEdit';
import { apiPut, API_BASE } from '../utils/api';
import { friendlyErrorMessage } from './ErrorMessage';
import { useToastOrNoop } from '../contexts/ToastContext';
import {
  uploadOrderFiles,
  confirmRemoveAttachment,
  deleteOrderFile,
  refreshOrderFiles,
  groupItemAttachmentsByItemId,
  describePendingClaimFailures,
} from '../utils/orderFileMutations';
import useUploadProgress from '../hooks/useUploadProgress';
import usePendingOrderUploads from '../hooks/usePendingOrderUploads';

/**
 * The expanded row of the Department Orders TABLE layout, in edit mode.
 *
 * ── Why this exists ───────────────────────────────────────────────────
 * Product owner, 2026-08-07: *"I think there are too many steps. We can expand
 * the order, then click view details, then click edit. Why not just expand the
 * order straight into edit mode. So expand shows all information in edit mode
 * already unlocked and ready to accept edits. The user will still have the
 * ability to cancel/save edits."* Scope, his words: *"yes table layout only
 * for now"*.
 *
 * ── What it is NOT ────────────────────────────────────────────────────
 * It is not a second order form. `components/OrderForm.js` is the one the
 * create page and the edit page both use, and CLAUDE.md's Code Unification
 * Principle says to import rather than duplicate — two order forms would
 * diverge inside a month, and this app has the scars. This file owns only the
 * things a ROW owns and a PAGE cannot: the PUT, the discard confirmation, and
 * the unsaved-changes chip. Every field, every validation rule and every
 * layout decision comes from OrderForm unchanged.
 *
 * ── 🔑 IT DOES NOT DECIDE WHO MAY EDIT, AND MUST NEVER LEARN TO ───────
 * Whether this component is rendered at all is the PAGE's decision, taken from
 * the server-computed `can_edit` flag on the DETAIL payload — `detail.can_edit
 * === true`, where `detail` is this row's `GET /api/orders/:id` response
 * (`server/routes/orders.js:1104` — re-cited 2026-08-08, it was `:1103`, which
 * is now the `visibleItems` line, from `canEditOrder`). There is no
 * client-side editability test here and there must not be one: `canEditOrder`
 * resolves `canManageDepartmentOrders` in the ORDER'S OWN department and walks
 * the approval chain, and no client can reproduce that. Deriving authority
 * client-side is the defect class `docs/ROLES.md` §E and
 * `client/src/clientAuthorityManifest.test.js` exist to stop.
 *
 * 🪤 **The LIST row's `can_edit` is NOT the flag to use, and reading it was a
 * real defect.** `utils/refreshOneOrder.js` merges `status`,
 * `cancellation_reason`, `rejection_reason` and `admin_notes` into the list row
 * after an action (plus `can_action: false` unless the caller opts out with
 * `{ clearCanAction: false }`) and never `can_edit` — so one user, in one
 * session, approving an order they were step-1 approver on left the row saying
 * `can_edit: true` while the status became `approved`, which `canEditOrder`
 * refuses. That opened this unlocked form for an order the PUT would then
 * refuse. `refreshOneOrder` writes fresh detail into the page's `orderDetails`
 * in the same call, so the detail flag is current. Pinned by
 * `client/src/pages/DepartmentOrders.expandEdit.test.js` §7, both directions.
 *
 * ⚠️ AMENDED 2026-08-08 — THAT SENTENCE WAS TRUE OF THE ACTION PATH AND FALSE
 * OF THE SAVE PATH, AND THE GAP WAS A LIVE DEFECT. `handleEditorSaved` used to
 * refresh only the orders LIST, so after an inline save the detail cache still
 * held the PRE-EDIT payload: the collapsed row showed the new supplier, and
 * re-expanding showed the old one (product owner, in a real browser). Because
 * the gate above reads that same cache, the cached `can_edit` was stale too —
 * so the fix landing on `can_edit` was being quietly undone one save later.
 * `handleEditorSaved` now invalidates the cache entry and calls
 * `refreshOneOrder` before the list refetch. Pinned by §12 of that file.
 * The lesson generalises past this component: **every mutation path on that
 * page has to refresh the detail cache, not just the ones that look like
 * status changes** — an edit is a mutation exactly like an approval is.
 *
 * 🪞 ⚠️ **QUALIFIED 2026-08-08 — THAT RULE WAS READ AS "AND THE FOUR INLINE
 * HANDLERS ARE THEREFORE BROKEN". THEY ARE NOT, AND THE INVESTIGATION IS
 * WRITTEN DOWN SO NOBODY REPEATS IT.** `DepartmentOrders.js` has four other
 * `onSaved` handlers that hand-patch `orderDetails[orderId]` with a narrow
 * field set and never refresh: `InlinePoNumberEditor` (`po_number`),
 * `InlineStatusJump` (`status`), the admin-notes Save button (`admin_notes`)
 * and `handleCostCodeChange` / `handlePaymentTypeChange` (item accounting).
 * `InlineStatusJump` looks live — `can_edit` derives from `status` — and the
 * reported sequence (card layout, jump to `approved`, switch to Table, the
 * gate reads a stale `can_edit: true`) does reach the table layout on an
 * unrefreshed cache. **It still opens no hole, and the reason is a
 * short-circuit none of those handlers mentions.** All four are gated
 * client-side on `useOrderAuthority().canManageOrder(detail)` and server-side
 * on `requireDepartmentCapability(req, res, order.department_id,
 * 'canManageDepartmentOrders')`, and `canEditOrder` returns `{ canEdit: true }`
 * on **that same capability** (`server/helpers/editPermissions.js:113-116`)
 * **before** its status branch (`:129`) runs. So a caller who can fire any of
 * them holds `can_edit: true` at every status: the cached flag is stale but
 * CORRECT and the `PUT` is accepted. **The rule above is about the DETAIL
 * CACHE'S CONTENTS, which is a correctness question; it is not a claim that
 * every unrefreshed cache is an authority hole.**
 *
 * 🕶️ That safety is a PREMISE, and premises expire (CLAUDE.md, "A ratchet's
 * REASON is a premise"). Put one condition on `canEditOrder`'s manager branch —
 * "managers may not edit a `delivered` order" is the obvious one — and the hole
 * opens in production with every test green. It is pinned as such by
 * `server/tests/matrix/orders-accounting-and-edit-department-authority.test.js`
 * §4, which sweeps every status and names these handlers in its failure
 * message, and by `DepartmentOrders.expandEdit.test.js` §13, which pins the
 * other half — that all four controls stay behind `canManageOrder`.
 *
 * The server refuses independently — `PUT /api/orders/:id` runs the same
 * `canEditOrder` before it writes — so this is a convenience, never a gate.
 *
 * ── The order stays reachable at its own URL ──────────────────────────
 * Nothing here replaces `/orders/:id`. Deep links, print and share need a real
 * URL, so the row's reference-number button still navigates there.
 * 🔻 **The Actions menu's `View Details` item, which used to do the same, is
 * DELETED (2026-10-04, owner ruling: "seems like the expanded order gives us
 * the info") — removed from all three order list Actions menus; see
 * `pages/requiredActions.manifest.test.js`.** On Department Orders and To
 * Approve the reference-number button is now the ONLY route from the list
 * back to this page; `MyOrders.js` passes no such button at all, so My
 * Orders has no route from its own list to `/orders/:id` any more (the page
 * stays reachable via bell notifications, Hires pages and NewOrder's "View
 * Order").
 *
 * 🔻 **THE PANEL'S OWN "Open full order page →" LINK WAS DELETED 2026-09-13,
 * WITH ITS `onOpenFullPage` PROP AND ALL THREE PAGES' WIRING** — owner:
 * *"'Open full order page →' should go in the Actions menu which is already
 * there under 'View Details'."* It was REDUNDANT, not merely tidy: the menu
 * item ran the identical `navigate('/orders/' + id)` behind the identical
 * dirty guard.
 * 🚨 **THE GUARD IS THE PART THAT HAD TO SURVIVE, AND IT DID — VERIFIED, NOT
 * ASSUMED.** The link ran `blockIfDirty()` before navigating so an unsaved
 * inline edit could never be destroyed by it. `View Details` is in
 * `DepartmentOrders.js`'s `NAVIGATING_ACTIONS` (`runRowAction` calls
 * `blockIfDirty()` for exactly `view`/`edit`/`upload`), and on `MyOrders.js`
 * and `Approvals.js` the menu item's own `onClick` opens with
 * `if (blockIfDirty()) return;`. All three were read before the deletion.
 * The guard coverage MOVED with the affordance — see §10 of
 * `pages/DepartmentOrders.expandEdit.test.js`.
 */
function OrderInlineEditor({
  order,
  productions = [],
  suppliers = [],
  // PASS-THROUGH ONLY. The past-order supplier names for OrderForm's picker;
  // this component neither fetches nor interprets them. See
  // `client/src/utils/supplierOptions.js` for the two-tier model.
  supplierSuggestions = [],
  // PASS-THROUGH ONLY (2026-08-22). Which of `OrderForm`'s reference lists are
  // empty because the request FAILED rather than because the department has
  // none — see the `loadFailures` prop on `components/OrderForm.js`. This
  // component neither fetches nor interprets it; only the page that issued the
  // requests can know. Defaults to `{}`, so a caller that omits it renders
  // exactly what it rendered before.
  loadFailures = {},
  // PASS-THROUGH ONLY, and that is the whole of this component's involvement.
  // The order-row action group (`Reject` / `Approve` / `Actions ▾` and the
  // page's own status lifecycle) rendered inside `OrderForm`'s `Order Details`
  // header since 2026-08-11, plus the node that must sit directly beneath it —
  // today only `Approvals.js`'s reject-reason textarea.
  //
  // 🚨 **THE PAGE BUILDS BOTH NODES, ALREADY GATED. This component neither
  // builds an item, nor resolves a permission, nor decides what a button
  // does** — it forwards two finished nodes, exactly as it forwards
  // `supplierSuggestions`. Both default `null`, so omitting them renders the
  // form exactly as it rendered before they existed.
  headerActions = null,
  headerActionsBelow = null,
  // PASS-THROUGH ONLY (2026-09-28) — `OrderForm`'s `itemExtraActions`, the
  // hires pages' per-line menu items. Default null → byte-identical.
  itemExtraActions = null,
  // PASS-THROUGH ONLY (2026-09-29) — `OrderForm`'s `itemHeaderTick`, the
  // hires pages' per-line "Off hired" tick. Default null → byte-identical.
  itemHeaderTick = null,
  // PASS-THROUGH ONLY (2026-10-01) — `OrderForm`'s `onRequestCancelOrder`: the
  // page's existing cancel flow, or null when the viewer may not cancel here.
  onRequestCancelOrder = null,
  // PASS-THROUGH ONLY (2026-09-13, renamed same day) — OrderForm's
  // `orderDetailsRow2Rest` slot, the SECOND cell of Order Details row 2
  // (beside Expected Delivery, under PO Number). Department Orders passes
  // its Internal-notes editor through here; nothing else passes it yet.
  // Default null → byte-identical to before the prop existed.
  orderDetailsRow2Rest = null,
  // PASS-THROUGH ONLY (2026-09-13, renamed same day) — `OrderForm`'s two
  // `Order Details` row-2 cell slots. The edit branch supplies only
  // `orderDetailsRow2Lead` (the expected-delivery control); PO already has a
  // real field inside the form in edit mode, so `poNumberFieldSlot` is
  // forwarded for completeness and is `null` on every caller today. Both
  // default null → byte-identical.
  poNumberFieldSlot = null,
  orderDetailsRow2Lead = null,
  // PASS-THROUGH ONLY (2026-08-15) — OrderForm's received-tick plumbing (the
  // per-line checkbox map + handlers + the "Line Items — received (x/N)"
  // count). The PAGE builds it, only for a delivery-stage order the viewer's
  // `canReceiveItems` admits; this component forwards it exactly as it
  // forwards `headerActions`. Default null → the form renders byte-identically
  // to before the prop existed.
  received = null,
  gags = [],
  subDepartments = [],
  costCodes = [],
  paymentTypes = [],
  userAssignedProductions = null,
  onSaved,
  onCancel,
  onDirtyChange,
  // Bumped by the page each time it BLOCKED something (a collapse, a switch to
  // another row) because this form is dirty. Each bump fires the amber pulse,
  // so the warning appears where the user is looking. Same mechanism as
  // GagGroupTable's `dirtyWarnSignal` → `showDirtyWarning`.
  dirtyWarnSignal = 0,
  // Bumped by the page each time the user asked to COLLAPSE THIS DIRTY ROW.
  // Unlike `dirtyWarnSignal` (which refuses) this one ASKS — it opens the
  // three-way save/discard prompt. A counter for the same reason: the page has
  // no handle on this component, and a boolean could not express two collapse
  // attempts in a row.
  collapseRequestSignal = 0,
  // OPTIONAL `(outcome) => void` — how that prompt was ANSWERED: `'kept'`
  // (Keep editing / Escape / overlay), `'discarded'`, `'saved'` (only after a
  // `res.ok` PUT) or `'failed'`. The page's `hooks/useDirtyContinuation.js`
  // runs the click the prompt interrupted on the second and third, never on
  // the first or fourth (owner, 2026-09-28). Omit it and nothing changes.
  onCollapsePromptResolved = null,
  // ── SAVE-THEN-ACT (2026-08-09)──────────────────────────────────────
  // OPTIONAL. A ref the PAGE owns; this component fills it with a
  // `() => Promise<boolean>` that runs the form's own save and resolves
  // `true` ONLY after a `res.ok` PUT. Omit it and nothing here changes —
  // `DepartmentOrders.js` passes nothing and is byte-identical.
  //
  // It exists for a page-level action that must apply to the SAVED order
  // rather than to the stored one. Product owner, 2026-08-09, on To
  // Approve: *"approve butotn should approve and save the current state"*.
  // See `requestSave` below for why a boolean promise and not a callback.
  saveRequestRef = null,
  // ── PER-LINE-ITEM ATTACHMENTS (2026-09-13) ──────────────────────────────
  // Owner, asked whether "attach a file to a line item while editing" had to
  // exist before `/orders/:id` retires: *"Yes attachments per item is needed"*.
  //
  // 🚨 **THE UPLOAD IS IMMEDIATE AND DOES NOT TOUCH THE PUT — THAT IS THE WHOLE
  // DESIGN, AND IT IS THIS COMPONENT'S OWN STRUCTURAL PROMISE RESTATED.**
  // `components/OrderExpandedDetails.js`'s policy is *"UPLOAD IS IMMEDIATE, NOT
  // STAGED-UNTIL-SAVE"*, and its docblock spent a paragraph explaining that the
  // edit branch could not have this control because `OrderForm` STAGES per-item
  // files and only `pages/OrderPage.js` flushes them. The answer was not to
  // build a flush here — it was to stop staging: `OrderForm`'s
  // `itemFilesImmediate` bag POSTs on pick. So `handleSubmit` below is
  // untouched, and its guarantee ("a failed PUT cannot act") acquires no second
  // failure mode. An attachment also survives "Discard changes", which is what
  // a user expects of a quotation they just attached.
  //
  // 🔀 **A LINE ADDED DURING THIS EDIT — THREE RULINGS.** 2026-09-13: its
  // control was DISABLED ("save first"). 2026-09-14 (owner: *"it should be
  // allowed to upload a file before the new line item is saved … If the user
  // doesn't save, or they cancel/delete the new line the attachment should also
  // be deleted"*): its files STAGED in the browser and `putOrder` uploaded them
  // after the PUT. 2026-09-15 (R13; owner, on the "Staged" label: *"upload the
  // file straight away, delete it if the user does not save the line or they
  // delete the line"*): its files UPLOAD AT ONCE to the pending store
  // (`hooks/usePendingOrderUploads.js`) and `putOrder` CLAIMS them. What held:
  //   (1) THE CLAIM RUNS BEFORE `onSaved`, INSIDE `putOrder`. All attached → the
  //       page's post-save refresh sees them, then the row collapses. Any
  //       refused → `onSaved` is NEVER called: the row stays open, re-hydrated
  //       from the SAVED order (new lines now have ids), the banner and a toast
  //       name each file with the server's message, and the files stay listed
  //       under their now-saved line, so Save again retries the claim. A
  //       save-then-act request resolves `false`; "Save and close" does not close.
  //   (2) NEVER POSITIONAL: a new line's files are keyed by its `client_key`,
  //       which the PUT echoes on the row it inserted (`savedItemIdFor`). A line
  //       the server cannot identify is REPORTED, never guessed.
  //   (3) NOT SAVED → DELETED: ✕, removing the line, Discard, collapsing and
  //       unmounting all DISCARD the pending uploads on the server; page hide
  //       sends a keepalive discard; the server sweeps whatever a closed tab left.
  // 🔴 A SAVED line is unchanged: it uploads straight onto its own id and its
  // file survives Discard (the 2026-09-13 ruling, re-confirmed 2026-09-15).
  //
  // 🔴 **IT RESOLVES NO AUTHORITY, EXACTLY LIKE EVERY OTHER GATE HERE.** The
  // PAGE answers "may this viewer attach to a line item" and hands the answer
  // down; see `components/OrderExpandedRow.js` for which predicate and how
  // closely it matches `requireItemFilesAuthorityBeforeUpload`.
  canAttachItemFiles = false,
  // Per-FILE remove predicate, mirroring `DELETE /api/orders/:id/files/:fileId`,
  // which decides per file. Default refuses, so a caller that forgets it shows
  // no Remove rather than a button the server 403s — the same default and the
  // same reason as `OrderExpandedDetails`'s.
  canRemoveFile = () => false,
  // `(orderId, freshDetail) => void` — the PAGE's detail-cache writer, the same
  // one `OrderExpandedDetails` is given. The file list is a slice of that cache,
  // so every mutation has to come back through it; a local copy of `files` here
  // would be the second source of truth this component's docblock blames for the
  // stale `can_edit`.
  onFilesChanged = null,
  // ── THE SAVE-EPOCH GUARD (2026-09-13, review F1) ────────────────────────
  // OPTIONAL. A ref this component BUMPS each time a save lands, shared with
  // `components/OrderExpandedDetails.js` so both file surfaces in one expanded
  // row can tell "a newer, more authoritative writer has settled since my
  // detail re-fetch was issued". Supplied by `components/OrderExpandedRow.js`,
  // the one component that has both as children. Absent, the guard is inert.
  saveEpochRef = null,
  testId,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // The FORM's dirtiness, as OrderForm reports it. The editor's own `dirty`
  // (below) also counts pending uploads on lines not yet saved.
  const [formDirty, setDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showCollapsePrompt, setShowCollapsePrompt] = useState(false);
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);
  // Filled by OrderForm with its own `handleSubmit`. This is what makes "Save
  // and close" the SAME save the Save button performs rather than a second PUT.
  const submitRef = useRef(null);

  // The order this form hydrates from, frozen at mount.
  //
  // 🪤 OrderForm re-hydrates whenever its `initialOrder` REFERENCE changes
  // (OrderForm.js, the `mode === 'edit'` effect). The page's `orderDetails`
  // cache is rewritten by every in-place refresh on this page — a status
  // action on another row, a cost-code PATCH, `refreshOneOrder` — and each of
  // those hands back a NEW object. Passing the live cache entry straight
  // through would therefore wipe a half-typed edit whenever something else on
  // the page happened to refresh. Freezing it means the panel edits the order
  // as it was when opened, which is also what the edit PAGE does.
  // 🔀 SETTABLE since 2026-09-14, for exactly ONE writer: a save whose PUT
  // landed but whose pending-file claim did not re-hydrates the form from the
  // SAVED order (`putOrder`), so the new lines carry their ids and a second
  // save cannot insert them twice. Nothing else may write it.
  const [initialOrder, setInitialOrder] = useState(order);

  // ── PER-ITEM ATTACHMENTS: THE LIVE HALF ─────────────────────────────────
  //
  // 🚨 **READ FROM `order`, THE LIVE CACHE ENTRY — NEVER FROM `initialOrder`,
  // AND THAT IS THE EXACT INVERSE OF THE FREEZE ABOVE.** The freeze exists so a
  // page refresh cannot wipe a half-typed edit; the file list exists so an
  // upload IS visible the moment the page refreshes the detail. Same prop, two
  // opposite requirements, which is why they are two different reads: reading
  // `initialOrder.files` here would show the just-attached file only after the
  // row was closed and re-opened.
  //
  // 🔑 The toast is `useToastOrNoop`, not the throwing `useToast()` — the same
  // departure, for the same reason, that `components/OrderForm.js` documents at
  // its own `toast`: this component is rendered bare by a number of existing
  // suites, and `App.js` always mounts `ToastProvider` above every route that
  // reaches it, so the noop branch is unreachable in the running app.
  const toast = useToastOrNoop();
  const orderId = order?.id;
  const itemFilesByItemId = useMemo(
    () => groupItemAttachmentsByItemId(order?.files),
    [order?.files]
  );

  // 🚨 **THE UPLOAD-REFRESH vs SAVE RACE — review F1, 2026-09-13, REPRODUCED
  // RATHER THAN ARGUED.** This re-fetch and the page's post-save one are two
  // requests over one cache entry, and ours can land LAST: pick a file → POST
  // resolves → this GET goes out → the user clicks Save → PUT → `onSaved` →
  // the page invalidates and `refreshOneOrder` writes FRESH → **our PRE-PUT
  // response arrives and overwrites it**, and `toggleExpand` then serves that
  // stale entry on re-expand with no fetch. That is the house's "the app says
  // it saved and it did not" class, and a second save can write the old values
  // back. The upload control now sits directly beside Save, so the window is
  // ordinary use rather than a contrivance.
  //
  // 🔑 **A `useLatestRequest` SEQUENCE WOULD NOT HAVE FIXED IT** — it orders
  // OUR requests against each other, and the competing writer is the PAGE's.
  // The epoch is bumped by `handleSubmit` on a SUCCESSFUL save, captured here
  // before the GET and re-read after it.
  // 🪤 **BUMPED ON SUCCESS, NOT AT SUBMIT-START**, and the difference is a real
  // one: bumping when the PUT is issued would also suppress the refresh after a
  // FAILED save, where nothing overwrote anything and the just-attached file
  // would then be invisible until something else refreshed the row.
  const refreshFiles = useCallback(
    () => refreshOrderFiles(orderId, onFilesChanged, { saveEpochRef }),
    [orderId, onFilesChanged, saveEpochRef]
  );

  // 🔑 THE SERVER'S MESSAGE, VERBATIM. There is no client-side file-type
  // whitelist and there must not be one — `server/CLAUDE.md`'s *Upload
  // file-type policy* makes order files a general-purpose BLOCKLIST surface,
  // so a mirror here would drift the first time the owner rules on an
  // extension. The refusal is the server's to word.
  // 🪤 **ONE UPLOAD PER LINE AT A TIME — review F3, 2026-09-13.** The file
  // input is cleared after every pick (so a `change` fires again for the SAME
  // file, deliberately — re-picking after a failure has to work), which means
  // an impatient double-pick sent TWO identical POSTs and attached the file
  // twice. Keyed by item id, not a single flag: two DIFFERENT lines uploading
  // at once is legitimate and must stay possible.
  const [uploadingItemIds, setUploadingItemIds] = useState(() => new Set());
  // Per-file bars (2026-09-14): a saved line's immediate upload, and the save's
  // flush of staged files — see `components/UploadingFileRow.js`.
  const { begin: beginUpload, dismiss: dismissUpload, entriesIn: uploadsIn } = useUploadProgress();
  const markUploading = useCallback((itemId, on) => {
    setUploadingItemIds((prev) => {
      if (on === prev.has(itemId)) return prev;
      const next = new Set(prev);
      if (on) next.add(itemId); else next.delete(itemId);
      return next;
    });
  }, []);

  const handleItemFileUpload = useCallback(async (itemId, files) => {
    markUploading(itemId, true);
    const tracker = beginUpload(files, `item:${itemId}`);
    try {
      const uploaded = await uploadOrderFiles(orderId, files, { itemId, onProgress: tracker.onProgress });
      await refreshFiles();
      tracker.succeed();
      toast.success(`${uploaded.length} file(s) attached to the line item`);
    } catch (err) {
      tracker.fail(err.message);
      toast.error(err.message);
    } finally {
      // In `finally`, so a refusal re-arms the control rather than leaving the
      // line permanently unable to accept the file the user was told to fix.
      markUploading(itemId, false);
    }
  }, [orderId, refreshFiles, toast, markUploading, beginUpload]);

  // The panel's remove path, reached through the SAME shared helpers rather
  // than a second DELETE — one confirm wording, one endpoint, one refresh.
  // 🪤 The confirm runs BEFORE anything else, exactly as in the panel: a
  // declined question must leave the surface untouched (review F4).
  const handleItemFileRemove = useCallback(async (fileId) => {
    if (!confirmRemoveAttachment()) return;
    try {
      await deleteOrderFile(orderId, fileId);
      await refreshFiles();
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.message);
    }
  }, [orderId, refreshFiles, toast]);

  // ── FILES ON LINES THAT HAVE NO id YET (2026-09-15, R13) ───────────────
  // Uploaded at pick to the pending store, keyed by the line's `client_key`
  // (never its index); claimed by `putOrder` after the PUT; discarded by
  // everything that ends the edit without saving, including this unmounting.
  const pending = usePendingOrderUploads();

  // `null` when the viewer may not attach — `OrderForm` then renders no per-item
  // block at all, and the files stay visible in the expanded panel's ONE flat
  // list below, which is where they have always been listed.
  const itemFilesImmediate = canAttachItemFiles ? {
    filesByItemId: itemFilesByItemId,
    onUpload: handleItemFileUpload,
    uploadingItemIds,
    canRemoveFile,
    uploadingEntriesFor: (itemId) => uploadsIn(`item:${itemId}`),
    onDismissUpload: dismissUpload,
  } : null;

  // A pending upload is an unsaved change like any other — the chip, the
  // collapse guard and the page's poll pause all see it.
  const dirty = formDirty || pending.hasEntries;

  // Auto-clear the pulse (the BudgetCostCodes pattern the convention cites).
  useEffect(() => {
    if (!showDirtyWarning) return undefined;
    const timer = setTimeout(() => setShowDirtyWarning(false), 2000);
    return () => clearTimeout(timer);
  }, [showDirtyWarning]);

  // Seed from the MOUNT value so opening a row while the counter is already
  // non-zero does not pulse a form the user has not touched.
  const prevWarnSignal = useRef(dirtyWarnSignal);
  useEffect(() => {
    if (dirtyWarnSignal !== prevWarnSignal.current) {
      prevWarnSignal.current = dirtyWarnSignal;
      setShowDirtyWarning(true);
    }
  }, [dirtyWarnSignal]);

  // The page asked to collapse this row. Same mount-seeded counter idiom as the
  // pulse above, and for the same reason.
  const prevCollapseSignal = useRef(collapseRequestSignal);
  useEffect(() => {
    if (collapseRequestSignal !== prevCollapseSignal.current) {
      prevCollapseSignal.current = collapseRequestSignal;
      setShowCollapsePrompt(true);
    }
  }, [collapseRequestSignal]);

  // Lift dirtiness to the page, which is what actually blocks the collapse.
  // Ref-stabilised, and cleared on unmount, so a panel that disappears for any
  // reason cannot leave the page permanently blocked.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => { onDirtyChangeRef.current = onDirtyChange; });
  useEffect(() => { onDirtyChangeRef.current?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  // 🔻 THE `beforeunload` EFFECT THAT LIVED HERE MOVED TO THE PAGE (R14,
  // 2026-09-15). Leaving a page with a dirty row — reload, tab close, AND now
  // every in-app navigation and Back — is guarded ONCE per page by
  // `hooks/useUnsavedChangesGuard.js`, keyed on the `editDirtyId` this panel
  // lifts through `onDirtyChange` (pending uploads included, via `dirty`).
  // 🚨 Never re-add a guard here: react-router keeps ONE blocker, and a guard
  // per mounted editor is a second one. The page's own in-page navigating
  // links ask through `continueWhenClean` (`hooks/useDirtyContinuation.js`),
  // which opens the three-way prompt below and runs the click once clean.

  // ── THE SAVE-REQUEST HANDLE (2026-08-09) ──────────────────────────────
  //
  // 🚨 **IT RESOLVES `true` ONLY AFTER A `res.ok` PUT, AND THAT IS THE WHOLE
  // POINT.** It lets a caller hang an action off the save's SUCCESS PATH
  // instead of off a `catch` that has to remember not to act — the same
  // structural device `handleSaveAndClose` uses for the collapse. A caller
  // written `saveThen(() => approve())` therefore has no branch that can
  // approve an order whose edits were refused.
  //
  // The three ways it resolves `false`, each reached without a PUT or with a
  // failed one:
  //   - **No form mounted** — `submitRef.current` is null.
  //   - **VALIDATION refused the form** — `OrderForm.handleSubmit` is
  //     synchronous through `validateForm` and only calls `onSubmit` (this
  //     `handleSubmit`) when the form is valid. So if nothing STARTED by the
  //     time `submit()` returns, there is no PUT to wait for.
  //   - **The PUT failed** — the catch below.
  //   - **"Keep editing" on the post-approval pause** (2026-09-13) — the user
  //     declined to save an approved/ordered/delivered/cancelled order; see
  //     `cancelPendingSave`. The request stays OPEN while the pause is on
  //     screen, which is why `handleSubmit` marks the save started first.
  //
  // 🔑 It goes through `submitRef.current()` — OrderForm's OWN `handleSubmit`,
  // the very function its Save button fires — so there is ONE save path and
  // never two that drift. Never reach for
  // `querySelector('form#order-form').requestSubmit()`; see the `submitRef`
  // docblock in `OrderForm.js`.
  //
  // 🪤 A promise-returning ref rather than the counter-signal idiom used by
  // `dirtyWarnSignal` / `collapseRequestSignal` above. Those are FIRE AND
  // FORGET — the page tells this component something happened and never needs
  // an answer. This one needs the OUTCOME, and a signal cannot carry one back
  // without a second callback that the page must then remember to disarm; a
  // stale arming there would approve an order on the user's NEXT ordinary
  // "Save Changes" click. The promise disarms itself.
  const saveOutcomeRef = useRef(null);
  const submitStartedRef = useRef(false);

  const settleSaveRequest = (ok) => {
    const resolve = saveOutcomeRef.current;
    if (!resolve) return;
    saveOutcomeRef.current = null;
    resolve(ok);
  };

  const requestSave = () => new Promise((resolve) => {
    const submit = submitRef.current;
    if (!submit) { resolve(false); return; }
    saveOutcomeRef.current = resolve;
    submitStartedRef.current = false;
    submit();
    if (!submitStartedRef.current) settleSaveRequest(false);
  });

  // Assigned on EVERY render and cleared on unmount, exactly as `OrderForm`
  // publishes `submitRef` — so a collapsed row's handle can never be called
  // against a dead component.
  useEffect(() => {
    if (!saveRequestRef) return undefined;
    saveRequestRef.current = requestSave;
    return () => { saveRequestRef.current = null; };
  });

  // ── THE POST-APPROVAL PAUSE — ONE CHOKE POINT FOR EVERY SAVE (2026-09-13) ──
  //
  // Owner: *"We need the pause and check … it stops edits after the fact as a
  // supervisor may change the quantity after the buyer has placed the order
  // and not realise it's been placed."* The full order page had it; this row
  // did not, and the row is where managers now edit.
  //
  // 🔑 **IT LIVES HERE, IN `handleSubmit`, BECAUSE ALL THREE SAVE ROUTES ALREADY
  // CONVERGE HERE** — the Save button (`OrderForm.handleSubmit` → `onSubmit`),
  // "Save and close" (`submitRef`), and `saveRequestRef` (`requestSave` →
  // `submitRef`). One guard, not three copies; a route added later that reaches
  // the PUT without passing through this function would be a second save path,
  // which this file already forbids.
  //
  // 🔑 **`saveRequestRef` ORDERING: the page's "Unsaved changes" prompt (if the
  // row is dirty) → THIS pause → the PUT → the page's action.** The pause comes
  // before the write AND before the action, and "Keep editing" here resolves
  // the save request `false`, so the page's `if (saved) run()` never acts:
  // cancelling the pause cancels both. A page cannot act on an order whose
  // post-approval edit the user just declined to save.
  //
  // 🪤 `submitStartedRef` is set BEFORE the pause, not after it: `requestSave`
  // reads it synchronously the moment `submit()` returns, and a pause that had
  // not marked the save as started would be read as "validation refused the
  // form" and settle `false` while the dialog was still on screen.
  //
  // 🪤 The returned promise settles only when the pause is ANSWERED (and, on
  // Confirm, the PUT has settled). `OrderForm`'s `useSubmitGuard` holds for as
  // long as that promise is open, so a second Save click cannot stack a second
  // pause over the first.
  const [pendingSave, setPendingSave] = useState(null);

  // Which pending uploads this save will claim — and whether it may run at all:
  // refused while a file is still uploading or has failed, or when a line
  // holding files has a blank description (`buildPayload` drops that line, so
  // its files would have nowhere to go). See `usePendingOrderUploads#planClaims`.
  const handleSubmit = (payload, meta) => {
    submitStartedRef.current = true;
    const planned = pending.planClaims(meta && meta.items, payload && payload.items);
    if (planned.message) {
      setSubmitError(planned.message);
      settleSaveRequest(false);
      return undefined;
    }
    const plan = planned.lines;
    const removals = meta && meta.postApprovalRemovals;
    const removalNotes = (removals && removals.notes) || [];
    // ONE PAUSE PER INTENT — see `describeConfirmedRemovals` in `OrderForm.js`.
    // A save whose ONLY change is a removal the user already confirmed in the
    // removal pause goes straight to the PUT, carrying that removal's reason.
    // (A pending file on a NEW line is itself a change, so it can never ride
    // through here as "only the removal".)
    if (isPostApprovalStatus(order.status) && !(removals && removals.onlyChange)) {
      return new Promise((resolve) => {
        setPendingSave({ payload, removalNotes, resolve, plan });
      });
    }
    return putOrder(payload, buildEditReason('', removalNotes), plan);
  };

  const confirmPendingSave = async (reason) => {
    const held = pendingSave;
    setPendingSave(null);
    if (!held) return;
    try {
      await putOrder(held.payload, buildEditReason(reason, held.removalNotes), held.plan);
    } finally {
      held.resolve();
    }
  };

  // "Keep editing": no PUT, the form stays open and dirty, and a pending save
  // request (save-then-act) resolves `false` so the page's action never runs.
  const cancelPendingSave = () => {
    const held = pendingSave;
    setPendingSave(null);
    settleSaveRequest(false);
    if (held) held.resolve();
  };

  // `editReason` is sent ONLY when non-blank: an absent key keeps the audit
  // detail byte-identical to a save made before the reason box existed.
  const putOrder = async (payload, editReason, plan = null) => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const body = editReason ? { ...payload, edit_reason: editReason } : payload;
      const res = await apiPut(`${API_BASE}/api/orders/${order.id}`, body);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // `code` rides the Error so `friendlyErrorMessage` can map it
        // (409 `order_changed` → "reload to see the latest"; the form stays dirty).
        const failure = new Error(data.error || 'Failed to update order');
        if (data.code) failure.code = data.code;
        throw failure;
      }
      const saved = await res.json();
      // 🚨 THE SAVE EPOCH (review F1). Bumped HERE — after a `res.ok` PUT and
      // before `onSaved` hands the page the fresh order — so any detail
      // re-fetch this row issued BEFORE the save is now recognisably stale and
      // is DROPPED rather than applied over the page's freshly-saved cache.
      // See `refreshFiles` above for the sequence and for why it is not bumped
      // when the PUT is merely issued.
      if (saveEpochRef) saveEpochRef.current += 1;

      // 🔑 THE CLAIM — AFTER the PUT (the lines now have ids) and BEFORE
      // `onSaved` (which collapses and unmounts this row, and whose unmount
      // discards anything still pending). See `canAttachItemFiles`' docblock.
      const failures = plan && plan.length > 0
        ? await pending.claimPlanned(order.id, plan, saved && saved.items)
        : [];
      // Clear dirty BEFORE handing control back: the page collapses the row on
      // save, and a still-dirty flag would make its own guard block the
      // collapse it just decided to perform.
      setDirty(false);
      onDirtyChangeRef.current?.(false);

      if (failures.length > 0) {
        // 🚨 THE ORDER IS SAVED; SOME FILES ARE NOT ATTACHED. Do NOT collapse,
        // do NOT act. Re-hydrate from the saved order — the fresh detail if it
        // can be read, else the PUT's own response — so the new lines carry
        // their ids (a second save must not insert them again); the unattached
        // files stay listed under their now-saved line and Save retries them.
        let fresh = null;
        try {
          await refreshOrderFiles(order.id, (id, detail) => {
            fresh = detail;
            if (onFilesChanged) onFilesChanged(id, detail);
          }, { saveEpochRef });
        } catch (_) { /* unreadable — the PUT response below still carries the ids */ }
        setInitialOrder(fresh || saved);
        const message = describePendingClaimFailures(failures);
        setSubmitError(message);
        toast.error(message);
        settleSaveRequest(false);
        return;
      }
      // Settled BEFORE `onSaved`, which collapses the row and unmounts this
      // component — resolving afterwards would be a callback fired out of a
      // dying tree.
      settleSaveRequest(true);
      onSaved?.(saved);
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err));
      settleSaveRequest(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel with unsaved changes must never discard silently (requirement 3).
  // Clean form → collapse straight away; there is nothing to lose and a
  // confirmation for nothing is how confirmations stop being read.
  const handleCancel = () => {
    // A save (and its attachment uploads) in flight: the order may already be
    // saved, so there is nothing to discard (review M3). The button is disabled
    // too; this guards any other route to the handler.
    if (submitting) return;
    if (dirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onCancel?.();
  };

  // ── THE SAVE/DISCARD PROMPT ON COLLAPSE (2026-08-08) ──────────────────
  //
  // Product owner: *"If we make a change and try to close collpase the orfer I
  // would Like a pop up asking to save/discard as currently we have to scroll
  // down to the save/discard and its not 100% obviouse"*. Before this,
  // collapsing a dirty row BLOCKED and pulsed the amber chip — which is correct
  // about not discarding silently, but it refuses the user's action and then
  // points at controls that are off-screen below a long form. The prompt brings
  // both outcomes to where the user is looking.
  //
  // 🔑 **EVERY BUTTON SAYS WHAT IT DOES, AND "CANCEL" APPEARS NOWHERE.** Beside
  // "Discard changes", a button labelled "Cancel" reads as "cancel my edits" —
  // the exact opposite of what it does. Left to right:
  //   `Keep editing` · `Discard changes` · `Save and close`
  // ⚠️ **`Keep editing` takes INITIAL FOCUS, and Escape and an overlay click
  // both map to it.** It is the only always-recoverable outcome, so an
  // instinctive Enter or Escape can neither lose the edit nor commit an
  // unreviewed save.
  // Ref-held like `onDirtyChange`: the `'saved'` report lands after `onSaved`
  // has unmounted this row, so it must not depend on a re-render's props.
  const onPromptResolvedRef = useRef(onCollapsePromptResolved);
  useEffect(() => { onPromptResolvedRef.current = onCollapsePromptResolved; });
  const reportPrompt = (outcome) => onPromptResolvedRef.current?.(outcome);

  const handleKeepEditing = () => {
    setShowCollapsePrompt(false);
    reportPrompt('kept');
  };

  const handleDiscardAndClose = () => {
    if (submitting) return;
    setShowCollapsePrompt(false);
    setDirty(false);
    pending.discardAll(); // not saved → deleted on the server
    onDirtyChangeRef.current?.(false);
    onCancel?.();
    reportPrompt('discarded');
  };

  // 🚨 **A FAILED SAVE MUST NEVER COLLAPSE THE ROW, AND THAT IS STRUCTURAL
  // HERE RATHER THAN GUARDED.** This closes the dialog and runs the form's own
  // submit — nothing else. The collapse happens only inside `onSaved`, which is
  // reached only after a `res.ok` PUT. So:
  //   - VALIDATION fails → OrderForm sets its errors and never calls onSubmit →
  //     no PUT, no collapse, and the row is showing the form's own field errors.
  //   - The PUT fails → `handleSubmit`'s catch sets `submitError` → no onSaved,
  //     no collapse, and the row is showing the error banner.
  // There is no `catch` here that has to remember not to collapse; there is no
  // path from this function to a collapse at all.
  //
  // 🔑 It calls `submitRef.current()` — OrderForm's OWN `handleSubmit`, the very
  // function its Save button fires. Not a second PUT, and not
  // `querySelector('form#order-form').requestSubmit()`. See the `submitRef`
  // docblock in `OrderForm.js` for why both alternatives are wrong.
  // 🔀 2026-09-28: through `requestSave` — which IS `submitRef.current()` plus
  // the outcome — so the page learns whether the save landed. `'saved'` is
  // reported only on its `true`, i.e. after a `res.ok` PUT; every other way out
  // (validation, a refused PUT, the post-approval pause declined) is `'failed'`.
  const handleSaveAndClose = () => {
    setShowCollapsePrompt(false);
    requestSave().then((ok) => reportPrompt(ok ? 'saved' : 'failed'));
  };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        {/* 🔻 The "Editing {getOrderNumber(order)}" heading was REMOVED here
            2026-08-16 — product owner: *"I think we can loose 'Editing
            ORD-1007' when we expand an order, seems like a waste of space?"*
            The row it sat in is `Order Details`'s section header
            (`data-section-title`, `components/OrderForm.js`) two lines below
            this wrapper, which already states the order is being edited via
            its `headerActions` slot (Approve/Reject/Actions ▾). This text
            was a SEPARATE node in THIS component, not that header — do not
            confuse the two when reading old briefs that assumed one site.
            The dirty badge is UNCHANGED, so this div can render empty
            (collapses to zero height) when the row is clean.
            🔻 The "Open full order page →" link that used to sit at the
            right end of this row was DELETED 2026-09-13 as redundant with
            the Actions menu's `View Details` (ITSELF deleted 2026-10-04,
            owner ruling — see this file's docblock) — see this file's
            docblock, including why the dirty guard it carried is still in
            force. */}
        <div className="flex items-center gap-2">
          {dirty && (
            <span
              className={`text-xs font-medium text-amber-600 dark:text-amber-400 ${showDirtyWarning ? 'animate-pulse' : ''}`}
              data-testid="order-inline-editor-dirty"
            >
              &#9888; Unsaved changes
            </span>
          )}
        </div>
      </div>

      <OrderForm
        mode="edit"
        initialOrder={initialOrder}
        productions={productions}
        suppliers={suppliers}
        supplierSuggestions={supplierSuggestions}
        loadFailures={loadFailures}
        headerActions={headerActions}
        headerActionsBelow={headerActionsBelow}
        orderDetailsRow2Rest={orderDetailsRow2Rest}
        poNumberFieldSlot={poNumberFieldSlot}
        orderDetailsRow2Lead={orderDetailsRow2Lead}
        received={received}
        gags={gags}
        subDepartments={subDepartments}
        costCodes={costCodes}
        paymentTypes={paymentTypes}
        userAssignedProductions={userAssignedProductions}
        originalStatus={order.status || ''}
        submitting={submitting}
        submitError={submitError}
        submitLabel={order.status === 'rejected' ? 'Resubmit Order' : 'Save Changes'}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        cancelDisabled={submitting}
        onDirtyChange={setDirty}
        submitRef={submitRef}
        showItemFileUpload={canAttachItemFiles}
        itemFilesImmediate={itemFilesImmediate}
        pendingFiles={canAttachItemFiles ? pending : null}
        pendingFilesDisabled={submitting}
        onExistingFileToggleRemove={canAttachItemFiles ? handleItemFileRemove : null}
        postApprovalGuardStatus={isPostApprovalStatus(order.status) ? order.status : null}
        itemExtraActions={itemExtraActions}
        itemHeaderTick={itemHeaderTick}
        onRequestCancelOrder={onRequestCancelOrder}
        initialItemsCollapsed
      />

      {/* The post-approval pause — see `handleSubmit`. Reads the LIVE
          `order.status` (not the mount-frozen `initialOrder`), so a status that
          moved while the row was open is the stage the user is told about. */}
      <PostApprovalEditDialog
        open={pendingSave !== null}
        status={order.status}
        action="save"
        busy={submitting}
        onConfirm={confirmPendingSave}
        onCancel={cancelPendingSave}
      />

      <ConfirmDialog
        open={showDiscardConfirm}
        variant="danger"
        title="Discard unsaved changes?"
        message="This order has edits that have not been saved. Closing the row now will lose them."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          setDirty(false);
          pending.discardAll();
          onDirtyChangeRef.current?.(false);
          onCancel?.();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      {/* The collapse prompt. Deliberately NOT merged with the discard dialog
          above: that one is reached by the form's explicit "Cancel" button,
          where the user has already said "abandon this" and a two-way confirm
          is the honest question. This one is reached by a COLLAPSE, where the
          user has expressed no wish about their edits at all — so it must offer
          the third outcome rather than assume one. Only one can be open at a
          time (each is reached from a different, mutually exclusive gesture). */}
      <ConfirmDialog
        open={showCollapsePrompt}
        title="Save your changes?"
        message={UNSAVED_ORDER_PROMPT_MESSAGE}
        {...UNSAVED_PROMPT_BUTTONS}
        variant="save"
        busy={submitting}
        onCancel={handleKeepEditing}
        onAlt={handleDiscardAndClose}
        onConfirm={handleSaveAndClose}
      />
    </div>
  );
}

export default OrderInlineEditor;

// ── ARCHAEOLOGY (moved from root CLAUDE.md 2026-09-25) ──────────────────────
// The root `CLAUDE.md` Shared Modules row for this component keeps every RULE
// (the two opposite signal props, one save path through `submitRef`, it never
// decides who may edit, `can_edit` is read off the DETAIL payload and never the
// list row, every mutation path refreshes the DETAIL cache, the four
// hand-patching handlers are NOT a defect) and points here. The two incidents
// behind those rules, moved VERBATIM from that row:
//
// On `refreshOneOrder` never merging `can_edit` into the list row: — so one
// user in one session — a sub-department lead approving an order they were
// step-1 approver on — left the row saying `can_edit: true` at
// `status: 'approved'`, which server-side `canEditOrder` refuses. That opened
// the unlocked form for an order the `PUT` would then reject. The old
// navigate-to-`/orders/:id/edit` route self-corrected (`OrderPage.js`'s
// `initialMode === 'edit'` guard re-checks the fresh flag and falls back to
// view mode); the inline route had nothing equivalent.
//
// On the save path undoing that fix one save later (2026-08-08):
// `handleEditorSaved` called `fetchOrders(true)` alone: that refreshes the
// LIST, so the collapsed row was right, while `toggleExpand` fetches a detail
// **only when the cache is empty**, so re-expanding served the pre-edit payload
// — the product owner saw a changed supplier in the table and the old one in
// the form, and the cached `can_edit` was stale in exactly the same breath.
